import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

let nextId = 0;

/**
 * The bottom-sheet shell for the create form's pickers (Celebrating, Starts,
 * Runs for). Design system §6 bottom sheet: surface, 32 top radius, 36×5
 * grabber, content padding 12 16 20, over a scrim.
 *
 * Rendered through a CDK overlay, so it stacks above the create sheet (itself
 * a MatDialog) rather than inside it, and Escape closes only this sheet.
 *
 * Browser back is NOT intercepted: MatDialog closes the whole create sheet on
 * any history change, so a pushed state would take both down. Back behaves as
 * it does today. (Plan §3.1, amended.)
 */
@Component({
  selector: 'app-picker-sheet',
  templateUrl: './picker-sheet.component.html',
  styleUrls: ['./picker-sheet.component.scss'],
})
export class PickerSheetComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() title = '';
  /** A header action ("Done") for multi-step choices. Empty = none. */
  @Input() actionLabel = '';
  @Input() actionDisabled = false;

  @Output() closed = new EventEmitter<void>();
  @Output() action = new EventEmitter<void>();

  @ViewChild('sheet', { static: true }) sheetTpl!: TemplateRef<unknown>;

  readonly titleId = `picker-sheet-title-${nextId++}`;

  private overlayRef: OverlayRef | null = null;
  private subs = new Subscription();
  private returnFocusTo: HTMLElement | null = null;
  private touchStartY: number | null = null;

  constructor(private overlay: Overlay, private vcr: ViewContainerRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open']) return;
    if (this.open) this.attach();
    else this.detach();
  }

  ngOnDestroy(): void {
    this.detach();
  }

  close(): void {
    this.closed.emit();
  }

  onGrabberTouchStart(e: TouchEvent): void {
    this.touchStartY = e.touches[0]?.clientY ?? null;
  }

  /** Swipe down on the grabber to close. */
  onGrabberTouchEnd(e: TouchEvent): void {
    const end = e.changedTouches[0]?.clientY;
    if (this.touchStartY !== null && end !== undefined && end - this.touchStartY > 60) this.close();
    this.touchStartY = null;
  }

  private attach(): void {
    if (this.overlayRef) return;
    this.returnFocusTo = document.activeElement as HTMLElement | null;

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'pcg-picker-backdrop',
      panelClass: 'pcg-picker-panel',
      positionStrategy: this.overlay.position().global().bottom('0').centerHorizontally(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100%',
      maxWidth: '480px',
    });
    this.overlayRef.attach(new TemplatePortal(this.sheetTpl, this.vcr));

    this.subs.add(this.overlayRef.backdropClick().subscribe(() => this.close()));
    this.subs.add(this.overlayRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    }));
  }

  private detach(): void {
    if (!this.overlayRef) return;
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.overlayRef.dispose();
    this.overlayRef = null;
    // Back to the row that opened the sheet.
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target && document.contains(target)) setTimeout(() => target.focus(), 0);
  }
}
